import { ContainerLayout, ContentLayout } from '@hyperflake/react-ui-library';
import { ProgressTabModal, ProgressTabProvider } from '@library/progress-tab';
import { AppNavbar, AppSidebar } from 'modules/shared/features/navigation';
import { Outlet } from 'react-router-dom';
import React from 'react';
import { CrossIcon } from '@icon/index';
import { ArrowLeftIcon } from '@library/icons';
import AppNavbarMobile from 'modules/shared/features/navigation/components/AppNavBarMobile/AppNavbarMobile';

// --- hook: detect mobile (matches Tailwind lg breakpoint = 1024px) ---
function useIsMobile(breakpoint = 1024) {
    const [isMobile, setIsMobile] = React.useState<boolean>(() =>
        typeof window !== 'undefined' ? window.innerWidth < breakpoint : false
    );

    React.useEffect(() => {
        const mq = window.matchMedia(`(max-width:${breakpoint - 1}px)`);
        const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
        setIsMobile(mq.matches);
        mq.addEventListener('change', handler);
        return () => mq.removeEventListener('change', handler);
    }, [breakpoint]);

    return isMobile;
}

// --- Desktop: unchanged (keeps ContainerLayout + sidebar) ---
function DesktopShell() {
    return (
        <ContainerLayout>
            <AppNavbar />
            <AppSidebar />
            <ContentLayout className="p-0">
                <Outlet />
            </ContentLayout>
        </ContainerLayout>
    );
}

// --- Mobile navbar with hamburger ---
interface MobileTopBarProps {
    onMenuClick: () => void;
    isOpen: boolean;
}
function MobileTopBar({ onMenuClick, isOpen }: MobileTopBarProps) {
    return (
        <div className="sticky top-0 z-40 flex items-center gap-2 border-b bg-white/80 backdrop-blur px-3 py-2">
            <button
                type="button"
                onClick={onMenuClick}
                aria-label="Open menu"
                aria-controls="mobile-sidebar"
                aria-expanded={isOpen}
                className="inline-flex h-10 w-10 items-center justify-center rounded-xl border hover:bg-gray-50 active:scale-95"
            >
                <ArrowLeftIcon />
            </button>
            {/* Replace with <AppNavbar /> if you want full navbar here */}
            <div className="text-base font-semibold">ProgressCenter</div>
            <div className="ml-auto flex items-center">{/* optional actions */}</div>
        </div>
    );
}

// --- Mobile drawer that wraps your existing AppSidebar ---
interface MobileSidebarDrawerProps {
    open: boolean;
    onClose: () => void;
}
function MobileSidebarDrawer({ open, onClose }: MobileSidebarDrawerProps) {
    // close on Esc
    React.useEffect(() => {
        if (!open) return;
        const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [open, onClose]);

    // prevent body scroll when open
    React.useEffect(() => {
        if (!open) return;
        const prev = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = prev;
        };
    }, [open]);

    return (
        <div aria-hidden={!open} className={`fixed inset-0 z-50 ${open ? '' : 'pointer-events-none'}`}>
            {/* Backdrop */}
            <div
                onClick={onClose}
                className={`absolute inset-0 bg-black/40 transition-opacity ${open ? 'opacity-100' : 'opacity-0'}`}
            />
            {/* Panel */}
            <div
                id="mobile-sidebar"
                role="dialog"
                aria-label="Sidebar"
                className={`absolute left-0 top-0 h-full w-72 max-w-[85vw] bg-white shadow-xl transition-transform ${
                    open ? 'translate-x-0' : '-translate-x-full'
                }`}
            >
                <div className="flex items-center justify-between border-b px-3 py-2">
                    <span className="font-semibold">Menu</span>
                    <button
                        type="button"
                        onClick={onClose}
                        aria-label="Close menu"
                        className="inline-flex h-9 w-9 items-center justify-center rounded-lg hover:bg-gray-50"
                    >
                        <CrossIcon size={18} />
                    </button>
                </div>
                <div className="h-[calc(100%-44px)] overflow-auto">
                    <AppSidebar />
                </div>
            </div>
        </div>
    );
}

// --- Mobile shell: no ContainerLayout; hamburger toggles drawer ---
function MobileShell() {
    const [open, setOpen] = React.useState(false);
    return (
        <div className="flex min-h-screen flex-col">
            <AppNavbarMobile onMenuClick={() => setOpen((v) => !v)} isMenuOpen={open} setOpen={setOpen} />
            <MobileSidebarDrawer open={open} onClose={() => setOpen(false)} />
            <div className="flex-1 overflow-auto pt-[56px]">
                <Outlet />
            </div>
        </div>
    );
}

export default function MainLayout() {
    const isMobile = useIsMobile(1024); // match Tailwind lg breakpoint
    return (
        <ProgressTabProvider>
            {isMobile ? <MobileShell /> : <DesktopShell />}
            <ProgressTabModal />
        </ProgressTabProvider>
    );
}
